import { CircularProgress } from '@mui/material';
import { useCommunityContext } from 'providers';
import { useEffect, useMemo } from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';

import { UserStatus } from '@jebel/constants';

import { useAppAuth } from 'providers/useAppAuth';
import { CenterLayout } from 'shared/components/layouts';
import { ROUTES } from 'shared/constants';
import {
  useCurrentUser,
  usePushNotifications,
  useToast,
  useCurrentUserRoles,
  useCurrentCommunityAllowedRoutes,
  useHasSession,
  useCurrentUserCommunities,
} from 'shared/hooks';
import { recordDebug } from 'shared/utils/record';

import { renderContent } from './routerUtils';

export type ProtectedRouteProps = RouteProps & {
  redirectTo?: string;
};

export function ProtectedRoute({
  component,
  children,
  render,
  redirectTo = ROUTES.public.login,
  ...props
}: ProtectedRouteProps) {
  const hasSession = useHasSession();

  const { selectedCommunity, selectCommunity } = useCommunityContext();

  const { requestPermission } = usePushNotifications();
  const { showWarning } = useToast();
  const { logout } = useAppAuth();
  const { user, loading: userLoading } = useCurrentUser();
  const { isPlatformAdmin, loading: userSystemRolesLoading } = useCurrentUserRoles();
  const { isRouteAuthorized, loading: allowedRoutesLoading } = useCurrentCommunityAllowedRoutes();
  const { data: communities, loading: communitiesLoading } = useCurrentUserCommunities();

  const loading = userLoading || userSystemRolesLoading || allowedRoutesLoading;

  useEffect(() => {
    if (hasSession && !userLoading && user?.status === UserStatus.Inactive) {
      showWarning('Your account is not active, please contact with administration');
      requestCloseSession();
    }
  }, [hasSession, userLoading, user]);

  useEffect(() => {
    if (!hasSession || userLoading || communitiesLoading) {
      return;
    }

    if (communities.length === 0) {
      // Users without any active school shouldn't be able to log-in.
      // https://github.com/jebelapp/jebel/issues/1727
      showWarning('You do not have any school, please contact with administration', {
        reportable: true,
      });

      requestCloseSession();
      return;
    }

    const firstCommunity = communities[0];

    if (!selectedCommunity && firstCommunity?.id) {
      // Ensures an authenticated user should always have a community.
      // https://github.com/jebelapp/jebel/issues/1863
      recordDebug('No community selected, selecting the first one.');
      selectCommunity(firstCommunity.id);
    }
  }, [hasSession, communitiesLoading, communities]);

  useEffect(() => {
    if (user?.id) {
      // https://github.com/jebelapp/jebel/issues/1517
      requestPermission();
    }
  }, [user]);

  const requestCloseSession = async () => {
    await logout();
  };

  const isRouteAllowed = useMemo(() => {
    if (!hasSession) {
      return false;
    }

    if (isPlatformAdmin) {
      // Bypass all checks for platform admins
      return true;
    }

    return isRouteAuthorized;
  }, [isPlatformAdmin, isRouteAuthorized]);

  if (!hasSession) {
    return <Redirect to={redirectTo} />;
  }

  if (loading) {
    return (
      <CenterLayout>
        <CircularProgress />
      </CenterLayout>
    );
  }

  if (!isRouteAllowed) {
    recordDebug('User tried to access unauthorized route');
    return <Redirect to={ROUTES.user.home.index} />;
  }

  return (
    <Route {...props} render={props => renderContent({ component, render, children, props })} />
  );
}
